import { AdContainer } from '../../../ad-container/ad-container';
import {
  ViewabilityEventConditions,
  ViewabilityEnvironment
} from '../../../enums';
import { ViewabilityObserver } from '../../../types/viewability-observer';

const VIEWABLE_THRESHOLD_50_PERCENTS = 50;
const VIEWABLE_THRESHOLD_100_PERCENTS = 100;
const MRAID_V3 = 3;

export class MraidObserverService implements ViewabilityObserver {
  readonly environment: ViewabilityEnvironment = ViewabilityEnvironment.InApp;
  private readonly listeners: Record<
    ViewabilityEventConditions,
    Array<() => void>
  > = {
    [ViewabilityEventConditions.OnScreen]: [],
    [ViewabilityEventConditions.On50PercentVisible]: [],
    [ViewabilityEventConditions.On100PercentVisible]: [],
    [ViewabilityEventConditions.OnNotVisible]: []
  };

  private readonly exposureChangedPointer = this.onExposureChanged.bind(this);

  constructor(private readonly adContainer: AdContainer) {}

  observe(element: HTMLElement) {
    this.disconnect();
    this.attachEvents();
    this.replayCurrentExposure();
  }

  on(event: ViewabilityEventConditions, callback: () => void) {
    this.listeners[event].push(callback);
  }

  disconnect() {
    this.adContainer.detachExposureChangedListener();
  }

  private attachEvents(): void {
    this.adContainer.onExposureChanged(this.exposureChangedPointer);
  }

  // An ad already on screen at attach time gets no MRAID event.
  private replayCurrentExposure(): void {
    const exposure = this.adContainer.getCurrentExposure();
    if (exposure === null || exposure <= 0) {
      return;
    }
    // v2 viewability is binary, so isViewable() is the full signal; on v3 it
    // only proves exposure > 0.
    if (this.adContainer.getMraidVersion() < MRAID_V3) {
      this.onExposureChanged(exposure);
      return;
    }
    this.callListeners(ViewabilityEventConditions.OnScreen);
  }

  private callListeners(condition: ViewabilityEventConditions): void {
    this.listeners[condition].forEach(cb => cb());
  }

  private onExposureChanged(exposure: number): void {
    // Any exposure above zero means the SDK actually put the ad on screen: it
    // is the only signal separating a real display from a precache, which stays
    // at zero for its whole life (ADV-32187).
    if (exposure > 0) {
      this.callListeners(ViewabilityEventConditions.OnScreen);
    }

    if (
      exposure >= VIEWABLE_THRESHOLD_50_PERCENTS &&
      exposure < VIEWABLE_THRESHOLD_100_PERCENTS
    ) {
      this.callListeners(ViewabilityEventConditions.On50PercentVisible);
    } else if (exposure >= VIEWABLE_THRESHOLD_100_PERCENTS) {
      this.callListeners(ViewabilityEventConditions.On50PercentVisible);
      this.callListeners(ViewabilityEventConditions.On100PercentVisible);
    } else {
      this.callListeners(ViewabilityEventConditions.OnNotVisible);
    }
  }
}
